import React, { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAlert } from "react-alert";
// Using an ES6 transpiler like Babel
// Use native range inputs for maximum compatibility across environments
import { getAssessmentById } from "./../../actions/assessmentActions";
import Loader from "../Loader";
import "./RegSubjects.css";
import { getYearQuestions, getAvailableYears } from "./../../actions/questionActions";
import { CLEAR_AVAILABLE_YEARS } from "../../constants/questionConstants";
import { getMockExamQuestions } from "../../actions/mockActions";
import { getSavedExamSubjects, clearErrors as clearExamSubjectErrors } from "../../actions/admin/examSubjectAction";
import { getUserSubscription } from "../../actions/subscriptionActions";
import { assetUrl, subjectLogoUrl } from "../../utils/assets";
import YearDropdown from "./YearDropdown";

const RegisteredSubjects = ({ history, match }) => {
    const alert = useAlert();
    const dispatch = useDispatch();
    const { savedSubjects, isSaved, error: examSubjectError } = useSelector((state) => state.examSubjects);
    const [subjectType, setSubjectType] = useState("");
    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const [selectedUniversity, setSelectedUniversity] = useState([]);
    const [sliderValue, setSliderValue] = useState(1999);
    const [yearIndex, setYearIndex] = useState(0);
    const [sliderQueVal, setSliderQueVal] = useState(100);
    const [examId, setExamId] = useState("");
    const [examType, setExamType] = useState("");
    const [examSlug, setExamSlug] = useState("");
    const [utmeSubjects, setUtmeSubjects] = useState("");
    const [maximumSubjects, setMaximumSubjects] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [subjects, setSubjects] = useState("");
    const [ProcessSub, setProcessSub] = useState("");
    const [assessmentId, setAssessmentId] = useState("");
    const [assessmentType, setAssessmentType] = useState("");
    const [assessmentName, setAssessmentName] = useState("");
    const [subscriptionId, setSubscriptionId] = useState("");
    const [isDataReady, setIsDataReady] = useState(false);
    const [hasSetInitialYear, setHasSetInitialYear] = useState(false);
    const [isTrialPlan, setIsTrialPlan] = useState(false);
    const [subscriptionGuardMessage, setSubscriptionGuardMessage] = useState(null);
    const { user } = useSelector((state) => state.auth);
    // Robust userId fallback: Redux store -> localStorage userInfo -> undefined
    const userId = user?._id || (JSON.parse(localStorage.getItem('userInfo') || '{}')._id) || undefined;
    var minYear = 1999;
    const currentYear = new Date().getFullYear();
    const { csloading } = useSelector((state) => state.courseSubjects);
    const { assessmentData } = useSelector((state) => state.singleAssessment);
    const { userSubscription } = useSelector((state) => state.examSubscriptions);

    // Resolve subscription id from multiple sources: state -> store -> savedSubjects -> localStorage
    const sanitizeId = (candidate) => {
        if (!candidate && candidate !== 0) {
            return undefined;
        }
        if (typeof candidate === 'object') {
            if (candidate === null) {
                return undefined;
            }
            if (candidate._id) {
                return sanitizeId(candidate._id);
            }
            if (candidate.id) {
                return sanitizeId(candidate.id);
            }
            return undefined;
        }
        const value = String(candidate).trim();
        if (!value || value === 'undefined' || value === 'null') {
            return undefined;
        }
        return value;
    };

    const derivePlanMeta = (entry) => {
        if (!entry) {
            return { isTrialPlan: false };
        }

        const subscriptionPlan = entry.subscriptionPlan || entry;
        const explicitTrial = typeof subscriptionPlan?.isTrial === 'boolean'
            ? subscriptionPlan.isTrial
            : (typeof entry?.isTrial === 'boolean' ? entry.isTrial : undefined);

        const planLabels = [
            subscriptionPlan?.planName,
            subscriptionPlan?.planSlug,
            subscriptionPlan?.planType,
            subscriptionPlan?.title,
            subscriptionPlan?.description,
            entry?.planName,
            entry?.planSlug,
            entry?.title,
            entry?.description,
        ]
            .filter(Boolean)
            .map((label) => String(label).trim().toLowerCase());

        const priceRaw = subscriptionPlan?.price ?? entry?.price;
        const priceValue = Number(priceRaw);
        const labelHint = planLabels.some((label) => label.includes('trial') || label.includes('demo'));
        const priceHint = !Number.isNaN(priceValue) && priceValue <= 0;
        const isTrialPlan = typeof explicitTrial === 'boolean'
            ? explicitTrial
            : (labelHint || priceHint);

        return { isTrialPlan, planLabels, priceRaw, priceValue };
    };

    const getResolvedSubscriptionId = () => {
        try {
            const fromState = sanitizeId(subscriptionId);
            const fromStore = sanitizeId(
                userSubscription?._id ||
                userSubscription?.subscriptionPlan?._id ||
                userSubscription?.subscriptionPlanId
            );
            const fromSaved = sanitizeId(
                (savedSubjects || [])
                    .find((s) => s?.exam?._id == examId)?.subscriptionPlan?._id
            );
            const lsRaw = sanitizeId(localStorage.getItem('userSubscription'));
            // Prefer UserSubscription ids (from store / dashboard) before plan metadata.
            const resolved = fromStore || fromState || lsRaw || fromSaved || null;
            if (!resolved) {
                console.warn('[RegisteredSubjects] No subscriptionId resolved', { examId, fromState, fromStore, fromSaved, localStorageValue: lsRaw, userId });
            }
            return resolved;
        } catch (e) {
            console.error('[RegisteredSubjects] Error resolving subscriptionId', e);
            return sanitizeId(subscriptionId) || null;
        }
    };

    useEffect(() => {
        // Diagnostic: log subscription sources on mount
        console.log('[RegisteredSubjects] Component mount/update - subscription sources:', {
            subscriptionId_state: subscriptionId,
            userSubscription_store: userSubscription?._id,
            localStorage_userSubscription: localStorage.getItem('userSubscription'),
            localStorage_examId: localStorage.getItem('examId'),
            userId: user?._id,
            savedSubjects_count: savedSubjects?.length
        });

        // Sanitize bad localStorage values to avoid sending 'undefined'/'null'
        const ls = localStorage.getItem('userSubscription');
        if (ls === 'undefined' || ls === 'null' || (typeof ls === 'string' && ls.trim() === '')) {
            localStorage.removeItem('userSubscription');
            console.warn('[RegisteredSubjects] Removed invalid localStorage.userSubscription value:', ls);
        }

        setExamId(localStorage.getItem("examId"));
        setExamType(localStorage.getItem("examType"));
        setExamSlug(localStorage.getItem("examSlug"));
        setUtmeSubjects(localStorage.getItem("utmeSubjects"));
        setMaximumSubjects(localStorage.getItem("maximumSubjects"));
        setCategoryId(localStorage.getItem("categoryId"));
        setSubjects(localStorage.getItem("subjects"));
        setProcessSub(localStorage.getItem("processedSubjects"));
        setAssessmentId(localStorage.getItem("assessmentId"));
        setAssessmentType(localStorage.getItem("assessmentSlug") || localStorage.getItem("assessmentMode"));
        setAssessmentName(localStorage.getItem("assessmentName") || localStorage.getItem("assessmentMode"));

        if (user && user._id) {
            dispatch(getUserSubscription(user._id, examId));
            dispatch(getSavedExamSubjects(user._id));
        }
        if (assessmentId) {
            dispatch(getAssessmentById(assessmentId));
        }
    }, [dispatch, alert, isSaved, userId, examId, assessmentId]);

    // Note: subscriptionId is set from savedSubjects useEffect below, not from userSubscription
    // This ensures we use the subscription from the saved exam subjects record

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);


    useEffect(() => {
        // Clear stale errors from other flows (action-type collisions / previous pages).
        dispatch(clearExamSubjectErrors());
    }, [dispatch]);

    useEffect(() => {
        if (!examSubjectError) {
            return;
        }

        const status = examSubjectError?.response?.status;
        const message = (examSubjectError?.response?.data?.message || examSubjectError?.message || '').trim();

        // Ignore legacy RegisteredSubject update noise on the mock subject picker.
        if (message.toLowerCase().includes('registered subject record not found')) {
            dispatch(clearExamSubjectErrors());
            return;
        }

        if (status === 400 && message.toLowerCase().includes('subscription')) {
            setSubscriptionGuardMessage(message);
            alert.error(message);
        } else if (message) {
            alert.error(message);
        } else {
            alert.error('Unable to update registered subjects. Please try again.');
        }

        dispatch(clearExamSubjectErrors());
    }, [examSubjectError, alert, dispatch]);

    useEffect(() => {
        if (isSaved) {
            setSubscriptionGuardMessage(null);
        }
    }, [isSaved]);


    var objectiveTime = 180;
    var objectiveTimeLabel = "03 Hours 00 Min";
    if (assessmentType === "mock") {
        objectiveTime = 60;
        objectiveTimeLabel = "01 Hour";
    }
    const [time, setTime] = useState(objectiveTime);
    const [timeLabel, setTimeLabel] = useState(objectiveTimeLabel);

    const [subjectSelections, setSubjectSelections] = useState([]);
    const [postUtmeSubjects, setPostUtmeSubjects] = useState([]);

    const [shuffleQuestions, setShuffleQuestions] = useState(false);
    const [shuffleOptions, setShuffleOptions] = useState(false);
    const [regSubjects, setRegSubjects] = useState([]);
    const [proceedSubjects, setProceedSubjects] = useState([]);


    useEffect(() => {
        if (!examId || !savedSubjects || savedSubjects.length === 0) {
            return;
        }

        const matchingRecord = savedSubjects.find((item) => item?.exam?._id == examId);
        if (!matchingRecord) {
            return;
        }

        const processedSubjectIds = Array.isArray(matchingRecord.subjectsProcessed)
            ? matchingRecord.subjectsProcessed
            : [];
        setProceedSubjects(processedSubjectIds);

        const availableSubjects = (matchingRecord.subjects || [])
            .filter((entry) => processedSubjectIds.includes(entry?.subject?._id))
            .map((entry) => entry?.subject)
            .filter(Boolean);

        setRegSubjects(availableSubjects);
        setPostUtmeSubjects(availableSubjects);

        const resolvedPlanId = sanitizeId(matchingRecord?.subscriptionPlan?._id);
        if (resolvedPlanId) {
            console.log('Setting subscriptionId from savedSubjects:', resolvedPlanId);
            localStorage.setItem('userSubscription', resolvedPlanId);
            setSubscriptionId(resolvedPlanId);
        }

        const planMeta = derivePlanMeta(matchingRecord?.subscriptionPlan || matchingRecord);
        if (!userSubscription) {
            setIsTrialPlan(Boolean(planMeta.isTrialPlan));
        } else if (planMeta.isTrialPlan) {
            setIsTrialPlan(true);
        }

        setIsDataReady(true);
    }, [savedSubjects, examId, userSubscription]);

    const isMockMode = (() => {
        const fromState = String(assessmentName || assessmentType || "").toLowerCase();
        const fromStorage = String(
            (typeof window !== "undefined" && localStorage.getItem("assessmentMode")) ||
                (typeof window !== "undefined" && localStorage.getItem("assessmentSlug")) ||
                (typeof window !== "undefined" && localStorage.getItem("assessmentName")) ||
                ""
        ).toLowerCase();
        return fromState.includes("mock") || fromStorage.includes("mock");
    })();

    useEffect(() => {
        if (!userSubscription) {
            setIsTrialPlan(false);
            return;
        }

        const planMeta = derivePlanMeta(userSubscription?.subscriptionPlan || userSubscription);
        setIsTrialPlan(Boolean(planMeta.isTrialPlan));

        const resolvedStoreId = sanitizeId(
            userSubscription?.subscriptionPlan?._id ||
            userSubscription?.subscriptionPlanId ||
            userSubscription?._id
        );

        if (!resolvedStoreId || resolvedStoreId === sanitizeId(subscriptionId)) {
            return;
        }

        console.log('[RegisteredSubjects] Detected updated subscription from store', {
            resolvedStoreId,
            previous: subscriptionId,
        });

        setSubscriptionId(resolvedStoreId);
        localStorage.setItem('userSubscription', resolvedStoreId);
        setHasSetInitialYear(false);

        if (selectedSubjects.length > 0 && examType && examId && !isMockMode) {
            console.log('[RegisteredSubjects] Re-fetching available years after subscription change', {
                examType,
                examId,
                selectedSubjects,
            });
            dispatch(
                getAvailableYears(
                    examType || 'objective',
                    selectedSubjects,
                    selectedUniversity,
                    resolvedStoreId,
                    userId
                )
            );
        }
    }, [userSubscription, subscriptionId, selectedSubjects, examType, examId, selectedUniversity, userId, dispatch, isMockMode]);

    // console.log("reg", regSubjects);

    const onSubjectChange = (e) => {
        const subId = e.target.value;
        const subjectSlug = e.target.dataset.slug;

        // Mock always allows multi-select. Non-mock POST UTME stays single-select.
        const allowMultiSelect = isMockMode || examSlug !== "post-utme";
        let subArray = allowMultiSelect ? [...selectedSubjects, subId] : [subId];

        if (selectedSubjects.includes(e.target.value)) {
            console.log('Subject is already selected, removing it');
            subArray = subArray.filter((sub) => sub !== e.target.value);
        }

        const maxAllowed = Number(maximumSubjects);
        if (
            allowMultiSelect &&
            Number.isFinite(maxAllowed) &&
            maxAllowed > 0 &&
            subArray.length > maxAllowed
        ) {
            alert.error(`You can select up to ${maxAllowed} subject${maxAllowed === 1 ? "" : "s"} for this exam.`);
            return;
        }

        console.log('Final subArray after check:', subArray, 'length:', subArray.length);
        localStorage.setItem("subjects", subArray);
        setSelectedSubjects(subArray);
        console.log('📋 Subject changed - selectedSubjects:', subArray);
        
        // Reset flag so slider updates to new available years
        console.log('🔄 Resetting hasSetInitialYear to false');
        setHasSetInitialYear(false);
        
        // If no subjects selected, clear available years and reset slider state
        if (subArray.length === 0) {
            console.log('Clearing available years - dispatching CLEAR_AVAILABLE_YEARS');
            dispatch({ type: CLEAR_AVAILABLE_YEARS });
            setYearIndex(0);
            setSliderValue(1999);
            console.log('Reset slider state - yearIndex: 0, sliderValue: 1999');
        }
        
        // Proactively fetch available years immediately after selection (practice only)
        if (!isMockMode && assessmentType !== "mock" && subArray.length > 0) {
            const resolvedSubId = getResolvedSubscriptionId();
            console.log('Fetching available years for subjects:', subArray, 'subscriptionId:', resolvedSubId);
            // Pass minimal questionLimit=1 and omit year so backend treats this as years-only request
            dispatch(getAvailableYears(
                examType || "objective",
                subArray,
                selectedUniversity,
                resolvedSubId,
                userId
            ));
        }

        if (examSlug == 'post-utme' && universityList) {
            const tempSub = regSubjects.filter((item) => subArray.includes(item?._id))
            const temp = universityList.filter((item) =>
                tempSub.some((sub) => sub.university.includes(item._id))
            );
            setFilteredUniversity(temp);
        }

        let slugArr = [...subjectSelections, subjectSlug];
        if (subjectSelections.includes(subjectSlug)) {
            slugArr = slugArr.filter((slug) => slug !== subjectSlug);
        }

        setSubjectSelections(slugArr);
        if (!isMockMode && assessmentType !== "mock") {
            // Only dispatch immediate question fetch if we already have available years (i.e., a subject was previously selected)
            if (Array.isArray(availableYears) && availableYears.length > 0) {
                dispatch(
                    getYearQuestions(
                        examId,
                        examType,
                        subArray,
                        Number(sliderValue),            // year
                        false,                           // shuffleQuestions
                        false,                           // shuffleOptions
                        [],                              // selectedTopics
                        Number(sliderQueVal) || 100,     // questionLimit
                        selectedUniversity,
                        getResolvedSubscriptionId(),
                        userId               // userId from Redux or localStorage
                    )
                );
            }
        } else {
            dispatch(getMockExamQuestions(examId, examType, subArray, [], null, getResolvedSubscriptionId(), userId));
        }
    };

    const onSubjectRadioChange = (e) => {
        const subId = e.target.value;
        const subjectSlug = e.target.dataset.slug;
        localStorage.setItem("selectedsubjectId", subId);
        var subArray = [subId];
    setSelectedSubjects(subArray);
        localStorage.setItem("subjects", subArray);

        var slugArr = subjectSlug ? [subjectSlug] : [];
        setSubjectSelections(slugArr);

        if (assessmentType !== "mock") {
            console.log('subscriptionId-2', subscriptionId);
            const resolvedSubId = getResolvedSubscriptionId();
            // Fetch available years for radio-mode selections too
            dispatch(getAvailableYears(
                examType || "objective",
                subArray,
                selectedUniversity,
                resolvedSubId,
                userId
            ));
            if (Array.isArray(availableYears) && availableYears.length > 0) {
                dispatch(
                    getYearQuestions(
                        examId,
                        examType,
                        subArray,
                        Number(sliderValue),            // year
                        false,
                        false,
                        [],
                        Number(sliderQueVal) || 100,    // questionLimit
                        selectedUniversity,
                        getResolvedSubscriptionId(),
                        userId
                    )
                );
            }
        } else {
            dispatch(getMockExamQuestions(examId, examType, subArray, [], "", getResolvedSubscriptionId(), userId));
        }
    };

    const maxYear = currentYear;

    const handleChangeStart = () => { };

    const handleChange = (eventOrValue) => {
        // Range now selects index into availableYears so only valid years are chosen
        const raw = (eventOrValue && eventOrValue.target)
            ? eventOrValue.target.value
            : eventOrValue;
        const nextIdx = Number(raw);
        setYearIndex(nextIdx);
        const nextVal = Array.isArray(availableYears) && availableYears.length > 0
            ? Number(availableYears[nextIdx])
            : Number(raw);
        setSliderValue(nextVal);
        if (selectedSubjects !== undefined && selectedSubjects.length > 0) {
            const university = selectedUniversity;
            dispatch(
                getYearQuestions(
                    examId,
                    examType,
                    selectedSubjects,
                    Number(nextVal),                // year
                    false,
                    false,
                    [],
                    Number(sliderQueVal) || 100,    // questionLimit
                    university,
                    getResolvedSubscriptionId(),
                    userId
                )
            );
        }
    };

    const { questions, qLoading, availableYears = [], yearsLoading } = useSelector((state) => state.yearQuestions);

    // Debug log to see if years are loaded
    console.log('RegisteredSubjects render - availableYears:', availableYears.length, 'yearsLoading:', yearsLoading);

    const { mloading, mockQuestions, examData, questionCount } = useSelector((state) => state.mockList);

    // Year dropdown search filter
    const [yearFilter, setYearFilter] = useState("");

    const handleChangeComplete = () => { };
    const { universityList, loading, isUniversityDeleted } = useSelector((state) => state.university);
    const [filteredUniversity, setFilteredUniversity] = useState([]);

    const handleTimeOnChange = (valOrEvent) => {
        const val = Number(valOrEvent && valOrEvent.target ? valOrEvent.target.value : valOrEvent);
        var hours = Math.floor(val / 60);
        var minutes = val % 60 === 0 ? "00" : val % 60;
        setTime(val);
        setTimeLabel(hours + " Hour " + minutes + " Min");
    };

    const handlequestionOnChange = (valOrEvent) => {
        const val = Number(valOrEvent && valOrEvent.target ? valOrEvent.target.value : valOrEvent);
        localStorage.setItem("noOfQuestion", val);
        localStorage.setItem("questionLimit", val);
        localStorage.setItem("questionCount", val);
        console.log('💾 [RegisteredSubjects] Saved question limit to localStorage:', val);
        setSliderQueVal(val)
    }

    // Note: getAvailableYears is called in onSubjectChange when subjects are selected
    // No need for separate useEffect here to avoid race conditions

    // When availableYears first loads, default selection to the first available year and fetch questions
    // Mock exams do not use year selection.
    useEffect(() => {
        if (isMockMode) {
            return;
        }

        console.log('🔄 [useEffect] Checking conditions:', {
            yearsLoading,
            availableYearsLength: availableYears?.length,
            availableYears: availableYears,
            selectedSubjectsLength: selectedSubjects?.length,
            examId,
            examType,
            hasSetInitialYear
        });
        
        // Only run when we have years and subjects selected, and years are done loading
        if (!yearsLoading && Array.isArray(availableYears) && availableYears.length > 0 && selectedSubjects.length > 0 && examId && examType) {
            // Set initial year to first available year if not already set
            if (!hasSetInitialYear) {
                // Use FIRST year (oldest) instead of last (latest) because older years are more likely to have questions
                const firstIdx = 0;
                setYearIndex(firstIdx);
                const firstYear = Number(availableYears[firstIdx]);
                setSliderValue(firstYear);
                localStorage.setItem("year", firstYear);
                setHasSetInitialYear(true);
                
                // Fetch questions for the first available year immediately
                console.log('✅ [RegisteredSubjects] Auto-fetching questions for first available year:', firstYear, 'subjects:', selectedSubjects);
                const resolvedSub = getResolvedSubscriptionId();
                console.log('🔑 [RegisteredSubjects] Resolved subscriptionId:', resolvedSub);
                dispatch(
                    getYearQuestions(
                        examId,
                        examType,
                        selectedSubjects,
                        firstYear,                      // Use the first year (oldest with most questions)
                        false,
                        false,
                        [],
                        Number(sliderQueVal) || 100,    // questionLimit
                        selectedUniversity,
                        resolvedSub,
                        userId
                    )
                );
            } else {
                console.log('⏭️ [RegisteredSubjects] Skipping auto-fetch - hasSetInitialYear is already true');
            }
        } else {
            console.log('❌ [RegisteredSubjects] Conditions not met for auto-fetch');
        }
    }, [availableYears, yearsLoading, hasSetInitialYear, selectedSubjects, examId, examType, sliderQueVal, selectedUniversity, userId, isMockMode]);

    // If user changes subjects and current selected year becomes invalid, auto-adjust to latest valid year
    useEffect(() => {
        if (isMockMode) {
            return;
        }
        if (Array.isArray(availableYears) && availableYears.length > 0) {
            const numericYears = availableYears.map(Number);
            if (!numericYears.includes(Number(sliderValue))) {
                const latest = numericYears[numericYears.length - 1];
                setSliderValue(latest);
                setYearIndex(numericYears.length - 1);
            }
        }
    }, [availableYears, isMockMode, sliderValue]);


    const submitHandler = (e) => {
        e.preventDefault();
        localStorage.setItem("subjectId", subjectType);
        localStorage.setItem("selectedUniversity", selectedUniversity);
        if (!isMockMode) {
            localStorage.setItem("year", sliderValue);
        } else {
            localStorage.removeItem("year");
        }

        if (assessmentType === "practice") {
            localStorage.setItem("timeDuration", time);
        }
        localStorage.setItem("shuffleQuestions", shuffleQuestions);
        localStorage.setItem("shuffleOptions", shuffleOptions);

        if (!selectedSubjects || selectedSubjects.length === 0) {
            alert.error("Please select subject.");
            return false;
        }

        // Mock flow: subjects only — no year / question-count / practice checks.
        if (isMockMode) {
            localStorage.setItem("checkedSubjects", selectedSubjects.join(","));
            localStorage.setItem("subjects", selectedSubjects.join(","));
            localStorage.setItem("processedSubjects", selectedSubjects.join(","));

            if (mloading) {
                alert.info("Loading mock questions for your selection. Please wait, then click Next.");
                return false;
            }

            const loadedCount = Number(questionCount);
            const loadedList = Array.isArray(mockQuestions) ? mockQuestions.length : 0;
            if (Number.isFinite(loadedCount) && loadedCount <= 0 && loadedList <= 0) {
                alert.error("Question not available for the selected subject(s).");
                return false;
            }

            history.push("/instructions");
            return;
        }

        var isQuestionAvailabel = "No";
        var quesAvailabSub = [];
        var sub = [];
        var removeValFromIndex = [];

        if (examType === "theory") {
            selectedSubjects.map((item, key) => {
                if (
                    questions.length > 0 &&
                    questions.some(
                        (sItem) => sItem.theories.length > 0 && sItem.subject._id === item
                    )
                ) {
                    var subjectQuesData = questions.find((sit) => {
                        return sit.subject._id === item;
                    });
                    quesAvailabSub.push(subjectQuesData.subject.title);
                    isQuestionAvailabel = "Yes";
                } else {
                    removeValFromIndex.push(item);
                }
            });
        } else {
            selectedSubjects?.map((item, key) => {
                if (
                    questions !== undefined &&
                    questions.length > 0 &&
                    questions?.some(
                        (sItem) =>
                            sItem.questions !== undefined &&
                            sItem.questions.length > 0 &&
                            sItem.subject._id === item
                    )
                ) {
                    var subjectQuesData = questions?.find((sit) => {
                        return sit.subject._id === item;
                    });
                    quesAvailabSub.push(subjectQuesData?.subject.title);
                    isQuestionAvailabel = "Yes";
                } else {
                    if (item !== undefined) {
                        removeValFromIndex.push(item);
                    }
                }
            });
        }

        var arrRemainingSubjects = selectedSubjects.filter(
            (item) => !removeValFromIndex.includes(item)
        );

        localStorage.setItem("checkedSubjects", arrRemainingSubjects);

        if (removeValFromIndex.length > 0 && sub !== undefined && isQuestionAvailabel === "Yes") {
            var questionAvaiMsg = "";
            if (quesAvailabSub !== undefined && quesAvailabSub.length > 0) {
                questionAvaiMsg = `${quesAvailabSub} for YEAR ${sliderValue}`;
            }
            alert.error(
                `Question available for subject(s) ${questionAvaiMsg}`,
                {
                    onOpen: () => {
                        setSelectedSubjects(arrRemainingSubjects);
                        const resolvedSub = getResolvedSubscriptionId();
                        dispatch(
                            getYearQuestions(
                                examId,
                                examType,
                                arrRemainingSubjects,
                                Number(sliderValue),
                                false,
                                false,
                                [],
                                Number(sliderQueVal) || 100,
                                selectedUniversity,
                                resolvedSub,
                                userId
                            )
                        );
                    },
                }
            );
            return false;
        }

        if (isQuestionAvailabel === "No") {
            alert.error(`Question not available for year ${sliderValue}`);
            return false;
        }

        if (time === 0) {
            alert.error("Please select time.");
            return false;
        }
        history.push(`/select-subject-topic`);
    };

    const goToPreviousPage = (e) => {
        e.preventDefault();
        history.push(`/mode-of-assessment`);
    };

    const formatHr = (value) =>
        Math.floor(value / 60) + " Hrs " + (value % 60) + " Min";

    const handleShuffleQuestions = (event) => {
        setShuffleQuestions(!shuffleQuestions);
    };

    const handleShuffleOptions = (event) => {
        setShuffleOptions(!shuffleOptions);
    };

    // Show loader while data is being prepared
    if (!isDataReady && savedSubjects?.length > 0) {
        return <Loader />;
    }

    return (
        <Fragment>
            <section
                className="page-banner-sec text-center"
                style={{
                    backgroundImage: `url("${assetUrl('assets/frontend/img/about-banner.jpg')}")`,
                }}
            >
                <div className="container">
                    <ul className="clearfix paginations-cust">
                        <li>
                            <Link to="/">Home</Link>
                        </li>
                        <li>
                            <Link to={`/mode-of-assessment`}>
                                Mode of Assessment{" "}
                                <i>
                                    <u>
                                        {assessmentData !== undefined && assessmentData !== ""
                                            ? assessmentData.title
                                            : ""}
                                    </u>
                                </i>
                            </Link>
                        </li>
                    </ul>
                    {examSlug === "jamb-utme" && (
                        <Fragment>
                            <h1>Select Subjects: 1-{`${maximumSubjects}`}</h1>
                            <h3>(You may select 1 or more subjects from the {maximumSubjects} registered subjects)</h3>
                        </Fragment>
                    )}
                    {examSlug === "post-utme" && (
                        <Fragment>
                            <h1>Select Subjects: 1-{`${maximumSubjects || "many"}`}</h1>
                            <h3>
                              {isMockMode
                                ? `(You may select one or more subjects for this mock)`
                                : `(You may select any number of subjects)`}
                            </h3>
                        </Fragment>
                    )}
                    {(examSlug === "wassce-waec-ssce" || examSlug === "jsce-bece") && (
                        <Fragment>
                            {isMockMode ? (
                              <>
                                <h1>Select Subjects: 1-{`${maximumSubjects || "many"}`}</h1>
                                <h3>(You may select one or more subjects for this mock)</h3>
                              </>
                            ) : (
                              <h2>Select a subject from the {maximumSubjects} registered subjects</h2>
                            )}
                        </Fragment>
                    )}
                </div>
            </section>

            <section className="subscription-page">
                <div className="container">
                    {subscriptionGuardMessage && (
                        <div
                            className="subscription-guard-banner"
                            style={{
                                backgroundColor: '#fff4e5',
                                border: '1px solid #f0ad4e',
                                borderRadius: '6px',
                                padding: '16px',
                                marginBottom: '24px',
                            }}
                        >
                            <p>{subscriptionGuardMessage}</p>
                            <p>
                                <Link to="/subscription-plans">Purchase a new subscription</Link>
                                {' or '}
                                <button
                                    type="button"
                                    className="subscription-guard-dismiss"
                                    onClick={() => setSubscriptionGuardMessage(null)}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#0d6efd',
                                        cursor: 'pointer',
                                        padding: 0,
                                    }}
                                >
                                    Dismiss
                                </button>
                            </p>
                        </div>
                    )}
                    <div className="end-plan">
                        <h1>Registered Subjects</h1>
                    </div>
                    <div className="custom-radios-plan custom-check-box examtype-spaner">
                        {csloading ? (
                            <Loader />
                        ) : (
                            <div className="wrapper clearfix">
                                {examSlug === "post-utme" ? (
                                    <Fragment>
                                        {postUtmeSubjects !== undefined && postUtmeSubjects.length > 0
                                            ? postUtmeSubjects.map((item, key) => {
                                                const indexId = key + 1;
                                                return (
                                                    <Fragment key={indexId}>
                                                        <input
                                                            type={"checkbox"}
                                                            name="select"
                                                            id={`option-${indexId}`}
                                                            value={item._id}
                                                            data-slug={item.slug}
                                                            checked={
                                                                selectedSubjects.length > 0 &&
                                                                    selectedSubjects.includes(item._id)
                                                                    ? true
                                                                    : false
                                                            }
                                                            onChange={onSubjectChange}
                                                        />
                                                        <label
                                                            htmlFor={`option-${indexId}`}
                                                            className={`option option-${indexId}`}
                                                        >
                                                            <div className="dot" />
                                                            <div className="extype">
                                                                <img
                                                                    src={
                                                                        item.logo
                                                                            ? subjectLogoUrl(item.logo)
                                                                            : assetUrl('assets/img/No-Image-Placeholder.svg.png')
                                                                    }
                                                                    alt="exam-logo"
                                                                    onError={(e)=>{e.currentTarget.onerror=null;e.currentTarget.src=assetUrl('assets/img/No-Image-Placeholder.svg.png');}}
                                                                />
                                                            </div>
                                                            <span>{item.title}</span>
                                                        </label>
                                                    </Fragment>
                                                );
                                            })
                                            : ""}
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        {regSubjects !== undefined && regSubjects.length > 0
                                            ? regSubjects.map((item, key) => {
                                                const indexId = key + 1;
                                                const inputType = "checkbox";
                                                return (
                                                    <Fragment key={indexId}>
                                                        <input
                                                            type={inputType}
                                                            name="select"
                                                            id={`option-${indexId}`}
                                                            value={item._id}
                                                            data-slug={item.slug}
                                                            checked={
                                                                selectedSubjects.length > 0 &&
                                                                    selectedSubjects.includes(item._id)
                                                                    ? true
                                                                    : false
                                                            }
                                                            onChange={onSubjectChange}
                                                        />
                                                        <label
                                                            htmlFor={`option-${indexId}`}
                                                            className={`option option-${indexId}`}
                                                        >
                                                            <div className="dot" />
                                                            <div className="extype">
                                                                <img
                                                                    src={subjectLogoUrl(item.logo)}
                                                                    alt="exam-logo"
                                                                    onError={(e)=>{e.currentTarget.onerror=null;e.currentTarget.src=assetUrl('assets/img/No-Image-Placeholder.svg.png');}}
                                                                />
                                                            </div>
                                                            <span>{item.title}</span>
                                                        </label>
                                                    </Fragment>
                                                );
                                            })
                                            : ""}
                                    </Fragment>
                                )}
                            </div>
                        )}
                    </div>

                    {!isMockMode && (
                        <>
                            <div className="exam-control-row">
                                <div className="exam-control-label">
                                    <label style={{ fontSize: "18px" }}>
                                        Select Year of Examination
                                    </label>
                                </div>
                                <div className="exam-control-inputs">
                                    <div className="form-group">
                                        <div className="slider">
                                            <YearDropdown
                                                years={availableYears || []}
                                                selectedYear={sliderValue}
                                                loading={yearsLoading}
                                                onChange={(yr) => {
                                                    setSliderValue(yr);
                                                    localStorage.setItem("year", yr);
                                                    if (selectedSubjects && selectedSubjects.length > 0) {
                                                        const resolvedSub = getResolvedSubscriptionId();
                                                        console.log('[RegisteredSubjects] YearDropdown change', {
                                                            yr,
                                                            examId,
                                                            examType,
                                                            selectedSubjects,
                                                            questionLimit: Number(sliderQueVal) || 100,
                                                            selectedUniversity,
                                                            resolvedSub,
                                                            userId
                                                        });
                                                        dispatch(
                                                            getYearQuestions(
                                                                examId,
                                                                examType,
                                                                selectedSubjects,
                                                                yr,
                                                                false,
                                                                false,
                                                                [],
                                                                Number(sliderQueVal) || 100,
                                                                selectedUniversity,
                                                                resolvedSub,
                                                                userId
                                                            )
                                                        );
                                                    }
                                                }}
                                            />
                                            {(!yearsLoading && (!availableYears || availableYears.length === 0)) && (
                                                <div className="no-years">{selectedSubjects && selectedSubjects.length > 0 ? 'No years found for selected subjects' : 'Select subject(s) to load years'}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="exam-control-row">
                                <div className="exam-control-label">
                                    <label style={{ fontSize: "18px" }}>Choose  Number Of Question (1 - 100)</label>
                                </div>
                                <div className="exam-control-inputs">
                                    <div className="form-group">
                                        <div className="slider">
                                            <input
                                                type="range"
                                                min={1}
                                                max={100}
                                                step={1}
                                                value={Number(sliderQueVal) || 1}
                                                onChange={handlequestionOnChange}
                                            />
                                            {sliderQueVal !== NaN && sliderQueVal !== undefined && (
                                                <div className="value">{sliderQueVal}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {isTrialPlan && !isMockMode && (
                        <div className="trial-upgrade-hint">
                            You are on a free trial. <Link to="/subscription-plans">Subscribe</Link> to unlock other years!
                        </div>
                    )}

                    {assessmentType === "practice" && !isMockMode && (
                        <div className="row">
                            <div className="col-sm-3">
                                <div className="form-group">
                                    <br />
                                    <label style={{ fontSize: "18px" }}>Choose Time</label>
                                </div>
                            </div>
                            <div className="col-sm-9">
                                <div className="form-group">
                                    <div className="slider">
                                        <input
                                            type="range"
                                            min={0}
                                            max={360}
                                            step={15}
                                            value={Number(time) || 0}
                                            onChange={handleTimeOnChange}
                                        />
                                        {timeLabel && (
                                            <div className="value">{String(timeLabel)}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isMockMode && examType !== "theory" && (
                        <div className="row">
                            <div
                                className="col-md-6 wow fadeInDown col-xs-12 col-sm-6 col-lg-6"
                                style={{ visibility: "visible", animationName: "fadeInDown" }}
                            >
                                <div className="boxes">
                                    <input
                                        type="checkbox"
                                        id="shuffle_question"
                                        name="shuffle_questions"
                                        onChange={handleShuffleQuestions}
                                    />{" "}
                                    <label htmlFor="shuffle_question" className="custom-checkboxes">
                                        Shuffle Questions
                                    </label>
                                </div>
                            </div>
                            <div
                                className="col-md-6 wow fadeInDown col-xs-12 col-sm-6 col-lg-6"
                                style={{ visibility: "visible", animationName: "fadeInDown" }}
                            >
                                <div className="boxes">
                                    <input
                                        type="checkbox"
                                        id="shuffle_answer"
                                        name="shuffle_options"
                                        onChange={handleShuffleOptions}
                                    />{" "}
                                    <label
                                        htmlFor="shuffle_answer"
                                        className="custom-checkboxes lts-rts"
                                    >
                                        Shuffle Answer Choices
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="next-prev-steps text-center">
                        {localStorage.getItem("savedId") === undefined || localStorage.getItem("savedId") === null && (
                            <button onClick={goToPreviousPage} className="chose-more">
                                <i className="fas fa-arrow-left" /> Prev
                            </button>
                        )}
                        &nbsp;
                        {(!isMockMode && (yearsLoading || qLoading)) || (isMockMode && mloading) ? (
                            <Loader />
                        ) : (
                            <button
                                type="button"
                                className="chose-more"
                                onClick={submitHandler}
                                disabled={
                                    isMockMode
                                        ? mloading || selectedSubjects.length === 0
                                        : yearsLoading ||
                                          qLoading ||
                                          !sliderValue ||
                                          !sliderQueVal ||
                                          (questions && questions.length === 0)
                                }
                            >
                                Next <i className="fas fa-arrow-right" />
                            </button>
                        )}
                    </div>
                </div>
            </section>
        </Fragment>
    );
};

export default RegisteredSubjects;
